import { useNavigate } from '@tanstack/react-router';
import { type FormEvent, type JSX, useState } from 'react';
import { useSession } from '../auth';
import { queryClient } from '../lib/query-client';
import { supabase } from '../lib/supabase';
import { AuthScreen } from '@/components/auth-screen';
import { Callout } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

const MIN_LENGTH = 10;

/**
 * Page of the invitation e-mail (lot 20): Supabase opens the session from the
 * link, the new member chooses a password, then enters the console.
 */
export function SetPasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const session = useSession();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (password.length < MIN_LENGTH) {
      setError(`Au moins ${MIN_LENGTH} caractères.`);
      return;
    }
    if (password !== confirm) {
      setError('Les deux saisies diffèrent.');
      return;
    }
    setBusy(true);
    setError(null);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (updateError) {
      setError(updateError.message);
      return;
    }
    queryClient.clear();
    await navigate({ to: '/proposer-passkey' });
  };

  return (
    <AuthScreen>
      <Card>
        <CardContent className="grid gap-5 p-6">
          <h1 className="m-0 text-title">Choisir votre mot de passe</h1>
          {session === undefined ? (
            <p className="m-0 text-sm text-muted-foreground">Ouverture de la session…</p>
          ) : session === null ? (
            <Callout variant="destructive">
              Lien expiré ou déjà utilisé. Demandez une nouvelle invitation à un administrateur, ou
              connectez-vous si votre mot de passe est déjà défini.
            </Callout>
          ) : (
            <form onSubmit={(e) => void submit(e)} className="grid gap-4">
              <p className="m-0 text-sm text-muted-foreground">
                Compte : <b className="text-foreground">{session.user.email}</b>
              </p>
              <Field label="Mot de passe">
                <Input
                  type="password"
                  required
                  minLength={MIN_LENGTH}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  autoComplete="new-password"
                />
              </Field>
              <Field label="Confirmer le mot de passe">
                <Input
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => {
                    setConfirm(e.target.value);
                  }}
                  autoComplete="new-password"
                />
              </Field>
              <Button type="submit" size="lg" disabled={busy} className="w-full">
                {busy ? 'Enregistrement…' : 'Enregistrer et entrer'}
              </Button>
            </form>
          )}
          {error !== null && <Callout variant="destructive">{error}</Callout>}
        </CardContent>
      </Card>
    </AuthScreen>
  );
}
